import React, { useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    RadialLinearScale,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar, Line, Pie, Radar } from 'react-chartjs-2';
import { Activity, AlarmClock, Briefcase, CheckCircle, LayoutDashboard } from 'lucide-react';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    RadialLinearScale,
    Tooltip,
    Legend
);

const ReportsPanel = () => {
    const [selectedDashboard, setSelectedDashboard] = useState('Project Management');

    const barData = {
        labels: ['Team A', 'Team B', 'Team C'],
        datasets: [
            { label: 'Completed', data: [12, 19, 14], backgroundColor: '#4ade80' },
            { label: 'Pending', data: [6, 3, 5], backgroundColor: '#facc15' }
        ]
    };

    const lineData = {
        labels: ['Sprint 1', 'Sprint 2', 'Sprint 3', 'Sprint 4'],
        datasets: [
            {
                label: 'Velocity',
                data: [30, 35, 40, 38],
                borderColor: '#60a5fa',
                backgroundColor: 'rgba(96,165,250,0.2)',
                fill: true,
                tension: 0.4
            }
        ]
    };

    const donutData = {
        labels: ['Engineering', 'Design', 'QA', 'PM'],
        datasets: [
            {
                data: [45, 20, 15, 20],
                backgroundColor: ['#34d399', '#f59e0b', '#60a5fa', '#a78bfa']
            }
        ]
    };

    const areaData = {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        datasets: [
            {
                label: 'Planned Hours',
                data: [100, 120, 130, 140],
                backgroundColor: 'rgba(96,165,250,0.4)',
                borderColor: '#60a5fa',
                fill: true,
                tension: 0.4
            },
            {
                label: 'Actual Hours',
                data: [90, 110, 125, 135],
                backgroundColor: 'rgba(34,197,94,0.4)',
                borderColor: '#22c55e',
                fill: true,
                tension: 0.4
            }
        ]
    };

    const horizontalData = {
        labels: ['Compliance', 'Delivery', 'Security', 'QA'],
        datasets: [
            {
                label: 'Risk Score',
                data: [6, 9, 7, 4],
                backgroundColor: '#f87171'
            }
        ]
    };

    const radarData = {
        labels: ['Communication', 'Speed', 'Accuracy', 'Autonomy', 'Collaboration'],
        datasets: [
            {
                label: 'Team A',
                data: [80, 70, 85, 90, 75],
                borderColor: '#a78bfa',
                backgroundColor: 'rgba(167,139,250,0.2)'
            }
        ]
    };

    const pieData = {
        labels: ['Development', 'Marketing', 'Ops', 'Contingency'],
        datasets: [
            {
                data: [40, 25, 20, 15],
                backgroundColor: ['#facc15', '#34d399', '#60a5fa', '#f472b6']
            }
        ]
    };

    return (
        <div className="p-6 text-white space-y-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-indigo-400">
                    <LayoutDashboard className="w-5 h-5" /> Reports Dashboard
                </h2>
                <select
                    value={selectedDashboard}
                    onChange={(e) => setSelectedDashboard(e.target.value)}
                    className="bg-neutral-800 text-white text-sm px-4 py-2 rounded-md border border-neutral-700 focus:outline-none"
                >
                    {['Project Management', 'System Info', 'Predictions'].map((d) => (
                        <option key={d} value={d}>
                            {d}
                        </option>
                    ))}
                </select>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800 hover:border-green-500 transition group">
                    <div className="flex items-center justify-between">
                        <p className="text-gray-400 text-sm">Overall Completion</p>
                        <CheckCircle className="w-5 h-5 text-green-400 group-hover:animate-pulse" />
                    </div>
                    <p className="text-2xl font-bold text-green-400 mt-2 transition-all duration-300 group-hover:scale-105">86%</p>
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800 hover:border-red-500 transition group">
                    <div className="flex items-center justify-between">
                        <p className="text-gray-400 text-sm">Overdue Tasks</p>
                        <AlarmClock className="w-5 h-5 text-red-400 group-hover:animate-ping" />
                    </div>
                    <p className="text-2xl font-bold text-red-400 mt-2 group-hover:scale-105 transition-all">5</p>
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800 hover:border-blue-500 transition group">
                    <div className="flex items-center justify-between">
                        <p className="text-gray-400 text-sm">Active Projects</p>
                        <Briefcase className="w-5 h-5 text-blue-400 group-hover:animate-bounce" />
                    </div>
                    <p className="text-2xl font-bold text-blue-400 mt-2 group-hover:scale-105 transition-all">8</p>
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800 hover:border-yellow-400 transition group">
                    <div className="flex items-center justify-between">
                        <p className="text-gray-400 text-sm">Team Utilization</p>
                        <Activity className="w-5 h-5 text-yellow-300 group-hover:animate-spin" />
                    </div>
                    <p className="text-2xl font-bold text-yellow-300 mt-2 group-hover:scale-105 transition-all">72%</p>
                </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800">
                    <h4 className="text-sm text-gray-400 mb-2">Team Performance</h4>
                    <Bar data={barData} options={{ plugins: { legend: { labels: { color: 'white' } } }, scales: { x: { ticks: { color: 'white' } }, y: { ticks: { color: 'white' } } } }} />
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800">
                    <h4 className="text-sm text-gray-400 mb-2">Sprint Velocity</h4>
                    <Line data={lineData} options={{ plugins: { legend: { labels: { color: 'white' } } }, scales: { x: { ticks: { color: 'white' } }, y: { ticks: { color: 'white' } } } }} />
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800">
                    <h4 className="text-sm text-gray-400 mb-2">Resource Distribution</h4>
                    <div className="max-w-[300px] h-[300px] mx-auto">
                        <Pie data={donutData} options={{ plugins: { legend: { labels: { color: 'white' } } } }} />
                    </div>
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800">
                    <h4 className="text-sm text-gray-400 mb-2">Planned vs Actual Hours</h4>
                    <Line data={areaData} options={{ plugins: { legend: { labels: { color: 'white' } } }, scales: { x: { ticks: { color: 'white' } }, y: { ticks: { color: 'white' } } } }} />
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800">
                    <h4 className="text-sm text-gray-400 mb-2">Department Risk Scoring</h4>
                    <Bar data={horizontalData} options={{ indexAxis: 'y', plugins: { legend: { labels: { color: 'white' } } }, scales: { x: { ticks: { color: 'white' } }, y: { ticks: { color: 'white' } } } }} />
                </div>

                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800">
                    <h4 className="text-sm text-gray-400 mb-2">Team Capability Radar</h4>
                    <div className="max-w-[300px] h-[300px] mx-auto">
                        <Radar data={radarData} options={{ plugins: { legend: { labels: { color: 'white' } } }, scales: { r: { pointLabels: { color: 'white' }, ticks: { display: false } } } }} />
                    </div>
                </div>
                <div className="bg-neutral-900 p-4 rounded-md border border-neutral-800">
                    <h4 className="text-sm text-gray-400 mb-2">Budget Allocation</h4>
                    <div className="max-w-[300px] h-[300px] mx-auto">
                        <Pie data={pieData} options={{ plugins: { legend: { labels: { color: 'white' } } } }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportsPanel;
